import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton"; // Importing Skeleton
import "react-loading-skeleton/dist/skeleton.css";
import { authenticatedFetch } from "../../network/api";
import "./payment.css";
import SetupPayment from "./assets/setupPayment.png";
import Header from "../../lawyer/components/header";

export default function PaymentOnboarding() {
  const [accountCreatePending, setAccountCreatePending] = useState(false);
  const [accountLinkCreatePending, setAccountLinkCreatePending] =
    useState(false);
  const [error, setError] = useState(false);
  const [connectedAccountId, setConnectedAccountId] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(false);
  const [loading, setLoading] = useState(true); // Loading state for skeleton

  // const lawyerInfo = useSelector((state) => state.lawyerData.lawyerData);
  const lawyerInfo = useSelector((state) => state.attorneyData.attorneyData);

  // Fetch the onboarding status when the component mounts
  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      const user_id = lawyerInfo.user_id;

      try {
        const response = await authenticatedFetch(
          "/api/attorney/webapp/fetchOnboardingStatus",
          {
            method: "POST",
            body: JSON.stringify({ user_id }),
          }
        );

        const data = await response.json();
        const { stripe_account_id, stripe_onboarding_status, error } = data;

        if (error) {
          setError(true);
        } else {
          setConnectedAccountId(stripe_account_id || null);
          setOnboardingStatus(stripe_onboarding_status || false);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false); // Set loading to false after the API call
      }
    };

    fetchOnboardingStatus();
  }, [lawyerInfo.user_id]);

  // Show skeletons while data is loading
  if (loading) {
    return (
      <div className="payment-management-container">
        <div className="banner">
          <Skeleton height={200} width="100%" />
        </div>
        <div className="content">
          <Skeleton count={3} /> {/* Placeholder for 3 paragraphs */}
        </div>
      </div>
    );
  }

  // If onboarding is complete, do not show any buttons or links
  if (connectedAccountId && onboardingStatus) {
    return (
      <div className="payment-management-container">
        <div className="banner">
          <img
            src={SetupPayment}
            alt="Quidpro Set up Payment"
            className="hero-image"
          />
        </div>

        <div className="content">
          <h2>All set!</h2>
          <p>
            Your payment setup is complete. You can now start accepting
            payments.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />

      <div className="payment-management-container">
        <div className="banner">
          <img
            src={SetupPayment}
            alt="Quidpro Set up Payment"
            className="hero-image"
          />
        </div>

        <div className="content">
          {/* Conditional Rendering based on Account Status */}
          {!connectedAccountId && (
            <h2 className="warning-text">
              Action Required: Complete Banking Setup
            </h2>
          )}
          {!connectedAccountId && (
            <p className="warning-paragraph">
              Please update your financial information. Employees of this firm
              will not be able to list their services until this step is
              completed.
            </p>
          )}

          {connectedAccountId && !onboardingStatus && <h2>Almost There!</h2>}
          {connectedAccountId && !onboardingStatus && (
            <p>
              We’ve set up your account! To start accepting payments, we need a
              bit more information.
            </p>
          )}

          {/* Create Account Button - If No Account Created */}
          {!accountCreatePending && !connectedAccountId && (
            <button
              className="action-button create-account"
              onClick={async () => {
                setAccountCreatePending(true);
                setError(false);
                const user_id = lawyerInfo.user_id; // Use user_id from the Redux store

                try {
                  const response = await authenticatedFetch(
                    "/api/attorney/webapp/createConnectAccount",
                    {
                      method: "POST",
                      body: JSON.stringify({ user_id }),
                    }
                  );
                  const json = await response.json();
                  setAccountCreatePending(false);

                  const { stripeAccountId, error } = json;

                  if (stripeAccountId) {
                    setConnectedAccountId(stripeAccountId);
                  }

                  if (error) {
                    setError(true);
                  }
                } catch (err) {
                  setAccountCreatePending(false);
                  setError(true);
                }
              }}
            >
              {accountCreatePending
                ? "Creating account..."
                : "Create your Stripe Account"}
            </button>
          )}

          {/* Add Information Button - If Account is Created but Onboarding is not complete */}
          {connectedAccountId &&
            !onboardingStatus &&
            !accountLinkCreatePending && (
              <button
                className="action-button create-link"
                onClick={async () => {
                  setAccountLinkCreatePending(true);
                  setError(false);
                  try {
                                      const response = await authenticatedFetch(
                    "/api/attorney/webapp/createOnboardingLink",
                    {
                      method: "POST",
                      body: JSON.stringify({ account: connectedAccountId }),
                    }
                  );
                    const json = await response.json();
                    setAccountLinkCreatePending(false);

                    const { url, error } = json;
                    if (url) {
                      window.location.href = url;
                    }

                    if (error) {
                      setError(true);
                    }
                  } catch (err) {
                    setAccountLinkCreatePending(false);
                    setError(true);
                  }
                }}
              >
                {accountLinkCreatePending
                  ? "Creating onboarding link..."
                  : "Add Your Information"}
              </button>
            )}

          {/* Error Handling */}
          {error && (
            <p className="error">
              Something went wrong! Please try again later.
            </p>
          )}

          {/* Status Callout */}
          {(connectedAccountId ||
            accountCreatePending ||
            accountLinkCreatePending) && (
            <div className="dev-callout">
              {connectedAccountId && (
                <p>
                  Your connected account ID is:{" "}
                  <code>{connectedAccountId}</code>
                </p>
              )}
              {accountCreatePending && <p>Creating a connected account...</p>}
              {accountLinkCreatePending && (
                <p>Creating a new Account Link...</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
